import { computed, Injectable, inject } from '@angular/core';
import { FileLikeObject } from 'ng2-file-upload';
import { IrisDmsSettingsSandbox } from '@iris/common/modules/dms/sandbox/dms-settings.sandbox';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslateService } from '@ngx-translate/core';
import { getSizeMB } from '@iris/common/modules/dms/helpers/dms-files.helper';
import { BulkFileUploader, BulkFileUploaderFactory } from '../../file-uploader/bulk-file-uploader';
import { AuthFileUploader, AuthFileUploaderFactory } from '@iris/common/modules/file-uploader/auth-file-uploader';
import { InnerHtmlToastComponent } from '../../toasts/components/inner-html-toast.component';

@Injectable({
  providedIn: 'root',
})
export class IrisDmsFileUploadHelper {
  private readonly dmsSettingsSandbox = inject(IrisDmsSettingsSandbox);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly authFileUploaderFactory = inject(AuthFileUploaderFactory);
  private readonly bulkFileUploaderFactory = inject(BulkFileUploaderFactory);

  readonly maxFileSizeInBytes = computed(() => this.dmsSettingsSandbox.maxFileSizeInBytes());
  readonly maxFileSizeInMB = computed(() => getSizeMB(this.maxFileSizeInBytes()));
  readonly milliSecToRemoveErrorMsg = 5000;
  readonly filesLimitForZip = 1;
  readonly allowedMimeTypeForZip = ['application/zip', 'application/x-zip-compressed'];

  static _fileUploader: BulkFileUploader;
  static _zipFileUploader: AuthFileUploader;

  constructor() {
    this.initFileUploader();
    this.initZipFileUploader();
  }

  checkNameOfOverSizedFile(fileList: File[]): string {
    const nameOfOverSizedFile = this.getNameOfOverSizedFile(fileList);

    if (nameOfOverSizedFile) {
      this.showFileSizeWarning(nameOfOverSizedFile);
      return nameOfOverSizedFile;
    } else {
      this.addFilesToQueue(fileList);
      return null;
    }
  }

  addFilesToQueue(filesList: File[], checkFileSize = true): BulkFileUploader {
    const filesInQueue = this.fileUploader.queue.map(fileItem => fileItem.file);
    const filesToAdd = filesList.filter(file =>
      !filesInQueue.some(queueFile => queueFile.name === file.name)
      && (checkFileSize && file.size <= this.maxFileSizeInBytes() || !checkFileSize));
    this.fileUploader.addToQueue([...filesToAdd]);
    return this.fileUploader;
  }

  clearQueue(): void {
    this.fileUploader.clearQueue();
  }

  get fileUploader(): BulkFileUploader {
    return IrisDmsFileUploadHelper._fileUploader;
  }

  get zipFileUploader(): AuthFileUploader {
    return IrisDmsFileUploadHelper._zipFileUploader;
  }

  private getMaxFileSizeMessage(message: string): string {
    return this.translateService.instant(message, { maxSize: this.maxFileSizeInMB() });
  }

  getMaxSizeWarning(notUploadedMessage: string, maxSizeMessage: string): string {
    const maxFileSizeMessage = this.getMaxFileSizeMessage(maxSizeMessage);
    return `<b>${notUploadedMessage}</b> <br> ${maxFileSizeMessage}`;
  }

  getMaxFileSizeWarning(fileName?: string): string {
    const notUploadedFileMessage = this.translateService.instant('text.dms.FileWillNotBeUploaded', { fileName });
    return this.getMaxSizeWarning(notUploadedFileMessage, 'text.dms.MaxFileSizeWarning');
  }

  get maxFileSizeText(): string {
    return this.getMaxFileSizeMessage('text.dms.MaxFileSize');
  }

  private showFileSizeWarning(fileName?: string): void {
    this.toastService.error({
      component: InnerHtmlToastComponent,
      data: {
        message: this.getMaxFileSizeWarning(fileName),
      },
    });
  }

  private getNameOfOverSizedFile(fileList: File[]): string {
    let nameOfOverSizedFile = '';

    if (!fileList.length) {
      return nameOfOverSizedFile;
    }

    for (const file of fileList) {
      if (file.size > this.maxFileSizeInBytes()) {
        nameOfOverSizedFile = file.name;
        break;
      }
    }
    return nameOfOverSizedFile;
  }

  isFileOversized(file: File | FileLikeObject): boolean {
    return file.size > this.maxFileSizeInBytes();
  }

  private initFileUploader(): void {
    if (IrisDmsFileUploadHelper._fileUploader) {
      return;
    }
    IrisDmsFileUploadHelper._fileUploader = this.bulkFileUploaderFactory.instance({
      url: '',
      removeAfterUpload: false,
      isHTML5: true,
    });
  }

  private initZipFileUploader(): void {
    if (IrisDmsFileUploadHelper._zipFileUploader) {
      return;
    }
    IrisDmsFileUploadHelper._zipFileUploader = this.authFileUploaderFactory.instance({
      url: '',
      allowedMimeType: this.allowedMimeTypeForZip,
      queueLimit: this.filesLimitForZip,
      isHTML5: true,
    });
  }
}
